/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useEffect, useMemo, useRef} from 'react';
import {makeStyles} from '@material-ui/core';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import usePersistentSDKIFrame from '@ion/dls/src/client/usePersistentSDKIFrame';
import ErrorHandler from '@ion/dls/src/components/errorhandler/ErrorHandler';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useSisenseSDK} from './hooks/useSisenseSDK';
import SisenseSkeleton from './SisenseSkeleton';
import WidgetTitle from './WidgetTitle';

// defines the stype props...
interface StyleProps {
  width?: number | string;
  height?: number | string;
  title?: string;
  styles?: GenericObject;
  framePadding?: number;
};

// defines the props definition for the component...
export interface SisenseSDKFrameProps extends StyleProps{
  dashboardId: string;
  route?: string;
  title?: string;
  widgetId?: string;
  useCustomHook?: Function;
  settings?: GenericObject;
  filter?: any[];
  queryParams?: string;
  fullScreen?: boolean;
  dashboardLayout?: boolean;
  isNavCollapsed?: boolean;
  persistIFrame?: boolean;
  iFrameId?: string;
  isEditMode?: boolean;
  isLoading?: boolean;
  onHomeView?: () => void;
  onSavedReportLoad?: Function;
  showFrame?: boolean;
  overrideStyle?: GenericObject;
};

// defines the style definitions for the component...
const useStyles = makeStyles((theme) => ({
  wrapper : {
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center',
    width           : '100%',
    height          : ({title}: StyleProps) => !title ? '100%' : '90%',
    overflow        : 'hidden',
    backgroundColor : dlsColor('white')
  },
  root : {
    backgroundColor                : dlsColor('white'),
    width                          : ({width}: StyleProps) => width ?? '100%',
    height                         : ({height}: StyleProps) => height ?? '100%',
    overflow                       : 'hidden',
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 124px)'
    }
  },
  title : {
    padding : '12px 0 0 0',
    display : 'flex'
  },
  frameContainer : {
    height       : '100%',
    width        : '100%',
    '& > iframe' : {
      height : ({title}: StyleProps) => !title ? '100%' : '92%',
      width  : '98%',
      border : 'none'
    }
  }
}));

/**
 * Defining the SisenseSDKFrame component. The component renders the Sisese dashboards
 * or individual widgets in a iframe by using sisense SDK and allow to more customization
 * in filters and to give more flowless UI.
 *
 * ```typescript
 *
 *  import {SisenseSDKFrame} from './SisenseSDKFrame';
 *
 *  <SisenseSDKFrame dashboardId='61ae95d512b1a20037dbb6bd'
 *    title={getIFrameDashboardTitle}
 *    height='calc(100vh - 130px)' />
 *
 * ```
 */
export const SisenseSDKFrame = ({
  dashboardId,
  widgetId,
  title,
  height,
  width,
  settings,
  filter,
  queryParams,
  route,
  iFrameId,
  styles,
  onHomeView,
  onSavedReportLoad,
  overrideStyle = {},
  showFrame = true,
  framePadding = 0,
  isLoading = false,
  isEditMode = false,
  fullScreen = false,
  dashboardLayout = false,
  persistIFrame = false,
  isNavCollapsed = false,
  useCustomHook = useSisenseSDK
}: SisenseSDKFrameProps) => {
  const classes     = useStyles({height, width, title});
  const rootEleRef  = useRef(null);
  const containerId = widgetId || dashboardId;
  const rootEle     = `${containerId}-root`;
  const isMounted   = useHasMounted();

  const {
    loading: isFrameLoading,
    error,
    onRefresh
  } = useCustomHook({
    iFrameId,
    isEditMode,
    dashboardId,
    widgetId,
    settings,
    filter,
    onHomeView,
    queryParams,
    persistIFrame,
    onSavedReportLoad
  });

  const loading = isFrameLoading || isLoading;

  usePersistentSDKIFrame({
    error,
    styles,
    showFrame,
    loading,
    fullScreen,
    dashboardLayout,
    framePadding,
    persistIFrame,
    isNavCollapsed,
    iFrameId : iFrameId ?? containerId
  });

  useEffect(() => {
    if (isMounted) {
      rootEleRef.current = document.getElementById(rootEle);
    }
  }, [isMounted, rootEle]);

  // returns the error message...
  const getErrorMessage = useMemo(() => (
    translate('common:errorHandler.customMessage', {route})
  ), []);

  const style = {
    ...((persistIFrame || !showFrame) && !loading && !error) ? {display: 'none'} : null,
    ...overrideStyle
  };

  return (
    <div className={classes.root} id={rootEle} style={style}>
      {title && (
        <div className={classes.title}>
          <WidgetTitle title={title} />
        </div>
      )}
      {!persistIFrame && (
        <div style={{display: (!loading && !error) ? 'block' : 'none', height: '100%'}}>
          <div id={iFrameId ?? containerId} className={classes.frameContainer} />
        </div>
      )}
      {loading && (
        <div className={classes.wrapper}>
          <SisenseSkeleton parentRef={rootEleRef} />
        </div>
      )}
      {!loading && error && (
        <div className={classes.wrapper}>
          <ErrorHandler hasError={Boolean(error)}
            errorText={getErrorMessage}
            onRefresh={onRefresh} />
        </div>
      )}
    </div>
  );
};

export default memo(SisenseSDKFrame);
